import { LeftOutlined, RightOutlined } from "@ant-design/icons"
import { Button, Flex, Typography } from "antd"
import dayjs, { type Dayjs } from "dayjs"
import React, { useEffect, useMemo, useState } from "react"

import { readAllDayDate } from "../../../../../utils/date/get-date-safe.ts"
import { type Event } from "../../../types/types.ts"

const { Text } = Typography

const WEEKDAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"]
const DAY_KEY_FORMAT = "YYYY-MM-DD"
// A single bad or open-ended record must not turn one event into a month-long
// scan — event days beyond this span are simply not dotted.
const MAX_SPAN_DAYS = 62

interface CalendarMiniMonthProps {
  currentDate: Date
  events: Event[]
  timezone: string
  onSelectDate: (date: Date) => void
}

/** The local calendar day an event starts and ends on, all-day-aware. */
const eventDayRange = (event: Event, timezone: string) => {
  const start = event?.all_day
    ? readAllDayDate(event.start_at)
    : dayjs(event?.start_at).tz(timezone)
  if (!start?.isValid()) return null

  const end = event?.end_at
    ? event?.all_day
      ? readAllDayDate(event.end_at, event.start_at)
      : dayjs(event.end_at).tz(timezone)
    : start

  return { start, end: end?.isValid() ? end : start }
}

export function CalendarMiniMonth({
  currentDate,
  events,
  timezone,
  onSelectDate
}: CalendarMiniMonthProps) {
  const selected = useMemo(() => dayjs(currentDate).tz(timezone), [
    currentDate,
    timezone
  ])
  const [displayedMonth, setDisplayedMonth] = useState(() =>
    selected.startOf("month")
  )

  // Follows the main calendar's own navigation (Prev/Next/Today, a header
  // click, a Lead deep-link) — but only when that outer date actually moves
  // to a different month, so browsing the mini-month itself with its own
  // prev/next isn't fought by this effect on every render.
  const selectedMonthKey = selected.format("YYYY-MM")
  useEffect(() => {
    setDisplayedMonth(dayjs.tz(selectedMonthKey, "YYYY-MM", timezone))
  }, [selectedMonthKey, timezone])

  const today = useMemo(() => dayjs().tz(timezone), [timezone])

  const eventDays = useMemo(() => {
    const days = new Set<string>()
    events?.forEach((event) => {
      const range = eventDayRange(event, timezone)
      if (!range) return

      let cursor = range.start
      let guard = 0
      // `!isAfter` rather than `isSameOrBefore` — the latter needs a dayjs
      // plugin this file doesn't otherwise depend on.
      while (!cursor.isAfter(range.end, "day") && guard < MAX_SPAN_DAYS) {
        days.add(cursor.format(DAY_KEY_FORMAT))
        cursor = cursor.add(1, "day")
        guard += 1
      }
    })
    return days
  }, [events, timezone])

  // A 6-row grid starting on the Sunday on/before the 1st, so short and long
  // months alike always show a full, unbroken set of weeks.
  const weeks = useMemo(() => {
    const start = displayedMonth.startOf("week")
    const cells: Dayjs[] = []
    for (let i = 0; i < 42; i += 1) cells.push(start.add(i, "day"))

    const rows: Dayjs[][] = []
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7))
    return rows
  }, [displayedMonth])

  return (
    <div className={"rounded-xl border border-gray-200 p-3"}>
      <Flex align={"center"} justify={"space-between"} className={"mb-2"}>
        <Text strong className={"text-sm"}>
          {displayedMonth.format("MMMM YYYY")}
        </Text>
        <Flex gap={0}>
          <Button
            type={"text"}
            size={"small"}
            aria-label={"Previous month"}
            icon={<LeftOutlined className={"text-xs"} />}
            onClick={() => setDisplayedMonth(displayedMonth.subtract(1, "month"))}
          />
          <Button
            type={"text"}
            size={"small"}
            aria-label={"Next month"}
            icon={<RightOutlined className={"text-xs"} />}
            onClick={() => setDisplayedMonth(displayedMonth.add(1, "month"))}
          />
        </Flex>
      </Flex>

      <div className={"grid grid-cols-7 gap-y-1 text-center"}>
        {WEEKDAY_LABELS.map((label) => (
          <Text key={label} type={"secondary"} className={"text-[10px] font-medium"}>
            {label}
          </Text>
        ))}

        {weeks.flat().map((day) => {
          const inMonth = day.isSame(displayedMonth, "month")
          const isToday = day.isSame(today, "day")
          const isSelected = day.isSame(selected, "day")
          const hasEvents = eventDays.has(day.format(DAY_KEY_FORMAT))

          return (
            <button
              key={day.format(DAY_KEY_FORMAT)}
              type={"button"}
              aria-label={day.format("MMMM D, YYYY")}
              onClick={() => onSelectDate(day.toDate())}
              className={[
                "relative mx-auto flex size-7 items-center justify-center rounded-full text-xs transition-colors",
                !inMonth ? "text-gray-300" : "text-gray-700",
                isSelected
                  ? "bg-teal-600 text-white font-semibold"
                  : isToday
                    ? "border border-teal-600 font-semibold text-teal-700"
                    : "hover:bg-gray-100"
              ].join(" ")}
            >
              {day.format("D")}
              {hasEvents && !isSelected && (
                <span
                  className={
                    "absolute bottom-0.5 size-1 rounded-full bg-teal-500"
                  }
                />
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
